/**
 * Dens and carcasses — ecology-lite (M2-05, design spec's ecology note).
 *
 * The design claim being defended: **hunting has consequences that outlast the
 * hunt**. A valley where wolves respawn on a timer is a valley where killing
 * them is a chore; a valley where a den you have emptied stays empty for days
 * is one where a player has changed something, and where the Great Hunt (M5)
 * can be a scarcity problem rather than a damage problem.
 *
 * That is the whole of it. Full ecology — prey populations feeding predator
 * populations, seasons, migration — is post-MVP and deliberately not modelled;
 * what is here is a cap, a slow refill, and a carcass that rots.
 *
 * Both tables are keyed by their own ids rather than by actor id, because
 * neither is an actor: a den is a place that produces animals and a carcass is
 * a thing that decays, and neither holds intents or occupies a tile
 * exclusively. M3-02 places dens for real; until then a caller places them.
 */
import { TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR } from './time.js';
/**
 * How long an emptied den stays empty.
 *
 * Two game days is the acceptance criterion, and it is stated as a floor rather
 * than a fixed delay: the point is that over-hunting is *felt*, and a number a
 * player can wait out precisely is a number they will wait out precisely.
 */
export const DEN_RESTOCK_TICKS = TICKS_PER_GAME_DAY * 2;
/** A partial den — one animal short — refills faster than an emptied one. */
export const DEN_TOPUP_TICKS = TICKS_PER_GAME_HOUR * 6;
/**
 * How many animals the valley carries at once (M3-10).
 *
 * A hard ceiling, and the first of the two mitigations the tick budget asks for
 * — the second is thinking on a cadence (`beasts.ts`). Placing warrens by biome
 * density rather than by hand means the *number* of dens is a consequence of the
 * terrain rather than a decision, so something has to hold the total, and a
 * number stated here is a number a perf test can be written against.
 *
 * Eighty is measured rather than guessed: it is roughly eight times M2-05's
 * population, it fills a commote densely enough that a walk out of the village
 * meets something, and a full commote of it holds the 20 ms budget with eight
 * players in it and room to spare.
 */
export const BEAST_CEILING = 80;
/**
 * How long a den goes between meals before it is hungry enough to come down to
 * the village (M5-02).
 *
 * Two game days — two real hours — and it is set against `DEN_RESTOCK_TICKS`
 * deliberately: a den that has just restocked has mouths it has not fed, and a
 * den that has been eating well out on the moor has no reason to walk past a
 * herder's fire. The number is what decides how often the valley pushes, and it
 * is the first thing a balance pass should reach for.
 */
export const DEN_HUNGER_TICKS = TICKS_PER_GAME_DAY * 2;
/** Has nothing from this den eaten lately? */
export function denIsHungry(den, tick) {
    return tick - (den.fedAt ?? 0) >= DEN_HUNGER_TICKS;
}
/** Mark this den fed. Called when one of its animals kills something. */
export function feedDen(den, tick) {
    den.fedAt = tick;
}
export function createDen(options) {
    return {
        id: options.id,
        x: options.x,
        y: options.y,
        speciesId: options.speciesId,
        cap: options.cap ?? 3,
        packId: options.packId ?? null,
        living: [],
        restockAt: 0,
        taken: 0,
        // A den opens the world fed, so the valley's first night is quiet. Wolves
        // arriving at the pen in the first ten minutes of a playtest would be a
        // scripted event wearing a simulation's clothes.
        fedAt: 0,
    };
}
/**
 * Forget the dead, and set the clock if they are all gone.
 *
 * Called on the hour rather than the tick: a den is a slow thing, and asking
 * ten times a second whether a wolf is still alive is ten times a second spent
 * on a question whose answer changes about once a week.
 */
export function reconcileDen(world, den, tick) {
    const before = den.living.length;
    den.living = den.living.filter((id) => {
        const combatant = world.combat[id];
        return world.actors[id] !== undefined && !combatant?.vitals.dead;
    });
    const lost = before - den.living.length;
    if (lost <= 0)
        return;
    den.taken += lost;
    // Emptied entirely is the expensive one. Losing one of three is a gap the
    // den closes by evening; losing all three is a den somebody has cleared out.
    const wait = den.living.length === 0 ? DEN_RESTOCK_TICKS : DEN_TOPUP_TICKS;
    den.restockAt = Math.max(den.restockAt, tick + wait);
}
/** Is this den due to produce another animal? */
export function denIsDue(den, tick) {
    return den.living.length < den.cap && tick >= den.restockAt;
}
/** How long a carcass lasts before the crows and the weather have it all. */
export const CARCASS_ROT_TICKS = TICKS_PER_GAME_DAY * 3;
/**
 * What field-dressing is worth (M5-03).
 *
 * A dressed carcass keeps two and a half times as long. Generous on purpose:
 * the mechanic has to reward the hunter who did the work, not punish the one
 * who was busy being chased by the rest of the pack — and a dressed stag that
 * still went off inside an afternoon would make hauling one home impossible,
 * which is the loop the litter exists for.
 */
export const DRESSED_KEEPS = 2.5;
/** How long this particular carcass has before the crows have had it all. */
export function rotTicks(carcass) {
    return carcass.dressedAt === undefined ? CARCASS_ROT_TICKS : CARCASS_ROT_TICKS * DRESSED_KEEPS;
}
/** Named stages, for the same reason stamina has bands: one word a player reads. */
export const CARCASS_STAGES = [
    { id: 'fresh', until: 0.15 },
    { id: 'cooling', until: 0.4 },
    { id: 'turning', until: 0.75 },
    { id: 'rotten', until: 1 },
];
export function carcassStage(carcass, tick) {
    const age = (tick - carcass.diedAt) / rotTicks(carcass);
    for (const stage of CARCASS_STAGES) {
        if (age <= stage.until)
            return stage.id;
    }
    return 'rotten';
}
const LAST_STAGE = CARCASS_STAGES[CARCASS_STAGES.length - 1];
export function isGone(carcass, tick) {
    return tick - carcass.diedAt > rotTicks(carcass) * LAST_STAGE.until;
}
/** What examining one reads as. Composed, never a lookup table of sentences. */
export function describeCarcass(carcass, tick) {
    switch (carcassStage(carcass, tick)) {
        case 'fresh':
            return `The body of ${carcass.name} lies here, still warm.`;
        case 'cooling':
            return `The body of ${carcass.name} lies here, stiffening.`;
        case 'turning':
            return `${capitalize(carcass.name)} lies here. The crows have been at it.`;
        default:
            return `What the crows have left of ${carcass.name} lies here, and it stinks.`;
    }
}
function capitalize(text) {
    return text.slice(0, 1).toUpperCase() + text.slice(1);
}
//# sourceMappingURL=ecology.js.map